import { motion } from "framer-motion";
import { Check, Sparkle, ArrowRight, Tag, FileMagnifyingGlass } from "@phosphor-icons/react";
import PriceCalculator from "./PriceCalculator";

const featuresFixed = [
    "Maison individuelle < 170 m²",
    "Visite sur site complète",
    "Rapport détaillé sous 24-48h",
    "Recommandations de travaux",
    "Conforme réglementation 2024",
];

const featuresDevis = [
    "Maison > 250 m²",
    "Immeuble et copropriété",
    "DPE collectif",
    "DTG · PPPT",
    "Études thermiques personnalisées",
];

export default function Pricing() {
    return (
        <section id="tarifs" data-testid="pricing-section" className="relative py-24 lg:py-28 bg-white">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
                <div className="text-center max-w-2xl mx-auto mb-14">
                    <span className="eyebrow justify-center">Tarifs transparents</span>
                    <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-lumos-ink leading-[1.05]">
                        Un prix fixe.<br />
                        <span className="font-serif-display text-lumos-blue">Sans surprise.</span>
                    </h2>
                    <p className="mt-5 text-lumos-muted">
                        Tarification claire pour les maisons individuelles. Devis personnalisé pour les biens plus complexes.
                    </p>
                </div>

                {/* INTERACTIVE PRICE CALCULATOR */}
                <PriceCalculator />

                <div className="grid lg:grid-cols-2 gap-7 max-w-5xl mx-auto">
                    {/* FIXED PRICE CARD */}
                    <motion.div
                        initial={{ opacity: 0, y: 26 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="relative rounded-3xl bg-gradient-to-br from-lumos-midnight to-[#101b3a] text-white p-8 lg:p-10 overflow-hidden card-lift border border-lumos-midnight"
                        data-testid="pricing-card-fixed"
                    >
                        <div className="absolute inset-0 bg-grid-dark opacity-40" />
                        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-lumos-blue/25 blur-3xl" />

                        <div className="relative">
                            <div className="flex items-center justify-between mb-6">
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-lumos-amber text-white text-xs font-bold tracking-widest uppercase">
                                    <Tag size={12} weight="fill" /> Prix fixe
                                </span>
                                <Sparkle size={22} weight="fill" className="text-lumos-amber" />
                            </div>

                            <div className="text-sm font-semibold tracking-widest uppercase text-white/60 mb-2">
                                Audit énergétique maison
                            </div>

                            <div className="flex items-end gap-2 mb-1">
                                <span className="font-display text-6xl lg:text-7xl font-extrabold tracking-tight">500</span>
                                <span className="font-display text-3xl font-bold text-lumos-amber mb-1">€</span>
                                <span className="text-sm font-semibold text-white/70 mb-2">TTC</span>
                            </div>
                            <p className="text-white/70 text-sm mb-7">
                                Pour toutes les maisons de moins de 170 m². Tarif tout compris.
                            </p>

                            <ul className="space-y-3 mb-9">
                                {featuresFixed.map((f) => (
                                    <li key={f} className="flex items-center gap-3 text-sm">
                                        <span className="w-5 h-5 rounded-full bg-lumos-green/25 grid place-items-center flex-shrink-0">
                                            <Check size={12} weight="bold" className="text-lumos-green" />
                                        </span>
                                        {f}
                                    </li>
                                ))}
                            </ul>

                            <a href="#contact" data-testid="pricing-cta-fixed" className="btn-primary !w-full justify-center">
                                Réserver mon audit
                                <ArrowRight size={18} weight="bold" />
                            </a>
                        </div>
                    </motion.div>

                    {/* DEVIS CARD */}
                    <motion.div
                        initial={{ opacity: 0, y: 26 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="relative rounded-3xl bg-white border border-lumos-border p-8 lg:p-10 overflow-hidden card-lift"
                        data-testid="pricing-card-devis"
                    >
                        <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-lumos-green/12 blur-3xl" />

                        <div className="relative">
                            <div className="flex items-center justify-between mb-6">
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-lumos-green/10 border border-lumos-green/30 text-lumos-green text-xs font-bold tracking-widest uppercase">
                                    <FileMagnifyingGlass size={12} weight="fill" /> Sur devis
                                </span>
                                <span className="text-lumos-muted/60 text-xs font-semibold uppercase tracking-widest">Personnalisé</span>
                            </div>

                            <div className="text-sm font-semibold tracking-widest uppercase text-lumos-muted mb-2">
                                Autres prestations
                            </div>

                            <div className="flex items-end gap-2 mb-1">
                                <span className="font-display text-6xl lg:text-7xl font-extrabold tracking-tight text-lumos-ink">Sur</span>
                            </div>
                            <div className="font-display text-3xl font-bold text-lumos-blue mb-1">mesure</div>
                            <p className="text-lumos-muted text-sm mb-7">
                                Devis adapté au projet : surface, complexité, type de bâtiment.
                            </p>

                            <ul className="space-y-3 mb-9">
                                {featuresDevis.map((f) => (
                                    <li key={f} className="flex items-center gap-3 text-sm text-lumos-ink">
                                        <span className="w-5 h-5 rounded-full bg-lumos-blue/15 grid place-items-center flex-shrink-0">
                                            <Check size={12} weight="bold" className="text-lumos-blue" />
                                        </span>
                                        {f}
                                    </li>
                                ))}
                            </ul>

                            <a href="#contact" data-testid="pricing-cta-devis" className="btn-secondary !w-full justify-center">
                                Demander un devis
                                <ArrowRight size={18} weight="bold" />
                            </a>
                        </div>
                    </motion.div>
                </div>

                <div className="text-center mt-9 text-sm text-lumos-muted">
                    Devis gratuit, sans engagement · Réponse sous 24h
                </div>
            </div>
        </section>
    );
}
